import { FormikProps } from 'formik';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import { getCurrentTimeNY, getNumberOfDays } from '../../../../../../global/helpers/date-helpers';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { DemographicsContext } from '../../../../../../global/models/demographics-model';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { Person } from '../../../../../../global/models/person-model';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { EmailsDisplay } from '../../../../../account-info/components/read-only/emails-display';
import { MailingAddressDisplay } from '../../../../../account-info/components/read-only/mailing-address-display';
import { MaritalStatusDisplay } from '../../../../../account-info/components/read-only/marital-status-display';
import { PersonFieldsContentDisplay } from '../../../../../account-info/components/read-only/person-fields-display';
import { PhonesDisplay } from '../../../../../account-info/components/read-only/phones-display';
import { AccountInfoRoutes } from '../../../../../account-info/constants/routes';
import { Spouse } from '../../../../../account-info/features/relationships/relationships-model';
import { convertDependentsToCoveragePersons, getCurrentSpouse } from '../../../../../account-info/features/relationships/relationships-selectors';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../../../../account-info/helpers/form-init-helpers';
import { AnnualEnrollmentCommentsDisplay } from '../../../../../annual-enrollment/components/read-only/annual-enrollment-comments-display';
import { ArcRoutes } from '../../../../constants/routes';
import { AnnualEnrollmentProps, NewHireProps } from '../../../../models/arc-epi-props';
import { useMemberHireDate } from '../../hooks/useMemberHireDate';
import { NewHireEnrollmentRoutes } from '../../models/new-hire-routes';
import { BenefitEnrollmentStep } from '../../models/pension-enrollment-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../slice/new-hire-enrollment-slices';
import { DependentsState } from '../../../../../account-info/features/dependents/dependents-model';
import { CoverageReviewDependent } from '../../enrollment/coverage/coverage-review-dependent';
import { MemberEmploymentCompensationReview } from '../member-review-components/member-employment-compensation-display';
import { MemberRetirementPlansDisplay } from '../member-review-components/member-retirement-plans-display';
import { ParticipantReview } from '../../enrollment/coverage/coverage-participant-review';
import { MemberCompletedCoverage, MemberNewHireEnrollmentCoverage, MemberPendingCoverage } from '../models/member-coverage-model';
import { MemberSelectedPlanDisplay } from '../member-review-components/member-selected-plan-display';
import { PlanTypes } from '../../enrollment/models/coverage-models';
import { MemberMedicalDentalPlanDisplay } from '../member-review-components/member-medical-dental-plan-display';
import { CalculatedTiers } from '../../models/new-hire-enrollment-models';
import { MemberPlanSummaryDisplay } from '../member-review-components/member-plan-summary-display';
import { ShouldShowMemberCafeteriaAcknowledgement } from '../helpers/member-coverage-helpers';
import { isNepEnrollment } from '../../hooks/isNepEnrollment';


interface Props {
	settings: NewHireProps;
	labelSettings: LabelSettingProps;
	formProps: FormikProps<any>;
	setEditUrl: any;
	basePath: string;
}

export function MemberCoverageReview(props: Props) {

	const { settings, labelSettings, formProps, setEditUrl, basePath } = props;
	const app = useContext(DemographicsContext);
	const aeProps = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state?.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const currentPerson = useSelector<TcsmStates, Person>(state => state?.personalInfo[app]?.currentPerson);
	const spouses = useSelector<TcsmStates, Spouse[]>(state => state?.relationships[app].spouses);
	const memberPendingPlans = useSelector<TcsmStates, MemberPendingCoverage>(state => state?.newHireEnrollmentState?.memberCoverageState?.coverage?.pending);
	const memberCompletePlans = useSelector<TcsmStates, MemberCompletedCoverage>(state => state?.newHireEnrollmentState?.memberCoverageState?.coverage?.complete);
	const dependentsState = useSelector<TcsmStates, DependentsState>(state => state.dependents[app]);
	const coverageState = useSelector<TcsmStates, MemberNewHireEnrollmentCoverage>(state => state?.newHireEnrollmentState?.memberCoverageState);
	const medicalTier = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayMedical);
	const dentalTier = useSelector<TcsmStates, CalculatedTiers>(state => state?.newHireEnrollmentState?.tierToDisplayDental);
	const buttonClasses = buttonStyles();
	const history = useHistory();

	const dispatch = useDispatch();
	const employeeRecordBase = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}${NewHireEnrollmentRoutes.EmployeeRecord}`) || basePath;

	const hireDate = useMemberHireDate();
	const currentDate = getCurrentTimeNY();
	const formattedHireDate = new Date(String(hireDate).replace(/-/g, '/'));
	const numberOfDays = getNumberOfDays(formattedHireDate, currentDate);
	const isNep = isNepEnrollment();

	const editButtonAction = (url: string, step?: BenefitEnrollmentStep) => {
		if (step === BenefitEnrollmentStep.COVERAGE) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(step));
			history.push(`${basePath}${NewHireEnrollmentRoutes.Coverage}`);
			return;
		}
		setEditUrl(url);
	};

	const renderEditButton = (url: string, step?: BenefitEnrollmentStep) => {
		return (
			<button className={buttonClasses.linkButton} type='button' onClick={() => editButtonAction(url, step)}>{aeProps?.editLabel}</button>
		);
	};

	return (
		<div className='grid-main-content'>
			<DividerSection removeDivider sectionText={settings?.personalInfoReviewSectionTitle} secondarySectionNode={renderEditButton(`${employeeRecordBase}${AccountInfoRoutes.PersonalInfoSegment}`)}>
				<PersonFieldsContentDisplay
					labelSetting={labelSettings}
					values={getInitialPersonInfo(currentPerson)}
					dobMasked
				/>
			</DividerSection>
			<MailingAddressDisplay
				labelSetting={labelSettings}
				values={getInitialMailingAddress(currentPerson)}
			/>
			<DividerSection removeDivider>
				<PhonesDisplay
					labelSetting={labelSettings}
					values={getInitialPhones(currentPerson)}
				/>
			</DividerSection>
			<DividerSection>
				<EmailsDisplay
					labelSetting={labelSettings}
					values={getInitialEmails(currentPerson)}
				/>
			</DividerSection>
			<MaritalStatusDisplay
				labelSetting={labelSettings}
				annualEnrollmentProps={aeProps}
				person={currentPerson}
				spouse={getCurrentSpouse(spouses)}
				editLink={renderEditButton(`${employeeRecordBase}${AccountInfoRoutes.RelationshipsSegment}`)}
			/>

			<CoverageReviewDependent
				dependents={convertDependentsToCoveragePersons(dependentsState.dependents)}
				settings={settings}
				editLink={renderEditButton(`${employeeRecordBase}${AccountInfoRoutes.RelationshipsSegment}`)}
			/>
			{
				!isNep &&
				<MemberEmploymentCompensationReview />
			}
			{
				!isNep &&
				<MemberRetirementPlansDisplay />
			}
			<ParticipantReview
				settingsNewHire={settings}
				settings={aeProps}
				formValues={formProps?.values}
				dependents={coverageState?.coverage?.eligible_dependents}
				editLink={renderEditButton(null, BenefitEnrollmentStep.COVERAGE)}
				hasMedical={coverageState?.coverage?.pending?.medical?.plans?.length > 0}
				hasDental={coverageState?.coverage?.pending?.dental?.plans?.length > 0}
				isMemberFlow
			/>

			<MemberMedicalDentalPlanDisplay
				planType={PlanTypes.M}
				groupLabel={settings?.medicalPlanTitle}
				plan={memberPendingPlans?.medical}
				formValues={formProps}
				tier={medicalTier}
				settings={settings}
				completedPlan={memberCompletePlans?.medical}
				declinedMessage={settings?.declineMedicalCoverageMessage}
			/>

			<MemberMedicalDentalPlanDisplay
				planType={PlanTypes.D}
				groupLabel={settings?.dentalPlanTitle}
				plan={memberPendingPlans?.dental}
				formValues={formProps}
				tier={dentalTier}
				settings={settings}
				completedPlan={memberCompletePlans?.dental}
				declinedMessage={settings?.declineDentalCoverageMessage}
			/>

			<MemberSelectedPlanDisplay
				groupLabel={settings?.groupLifePlanTitle}
				plan={memberCompletePlans?.group_life}
				settings={settings}
			/>

			<MemberSelectedPlanDisplay
				groupLabel={settings?.shortDisPlanTitle}
				plan={memberCompletePlans?.short_term_disability}
				settings={settings}
				pendingPlan={memberPendingPlans?.short_term_disability}
				selectedPendingPlan={formProps?.values?.W}
				declineMessage={settings?.declineStdCoverageMessage}
			/>

			<MemberSelectedPlanDisplay
				groupLabel={settings?.longDisPlanTitle}
				plan={memberCompletePlans?.long_term_disability}
				settings={settings}
				pendingPlan={memberPendingPlans?.long_term_disability}
				selectedPendingPlan={formProps?.values?.L}
				declineMessage={settings?.declineLtdCoverageMessage}
			/>
			<MemberSelectedPlanDisplay
				groupLabel={settings?.ueiPlanTitle}
				plan={memberCompletePlans?.UEI}
				settings={settings}
			/>
			<div className='grid-main-content'>
				<DividerSection removeDivider>
					<MemberPlanSummaryDisplay
						formikProps={formProps}
						memberPendingPlans={memberPendingPlans}
						memberCompletedPlans={memberCompletePlans}
						settings={settings}
					/>
				</DividerSection>
			</div>

			<AnnualEnrollmentCommentsDisplay
				annualEnrollmentProps={aeProps}
				myNotes={formProps?.values?.memo}
				specialRequests={formProps?.values?.specialRequests}
				editButton={renderEditButton(null, BenefitEnrollmentStep.COVERAGE)}
			/>

			<FormField
				name='agreement'
				label={settings?.coverageReviewCheckBoxText}
				component='checkBox'
				touched={formProps.touched}
				errors={formProps.errors}
				value={formProps?.values?.agreement ?? false}
				checked={formProps?.values?.agreement ?? false}
			/>
			{
				ShouldShowMemberCafeteriaAcknowledgement(memberCompletePlans, formProps.values, numberOfDays) &&
				<FormField
					name='cafeteriaAcknowledgement'
					label={settings?.memberCafeteriaAcknowledgement}
					component='checkBox'
					touched={formProps.touched}
					errors={formProps.errors}
					value={formProps?.values?.cafeteriaAcknowledgement ?? false}
					checked={formProps?.values?.cafeteriaAcknowledgement ?? false}
				/>
			}
		</div>
	);
}